import { Component, OnInit } from '@angular/core';
import { AzureSpeechSettings } from '@models/azure/azure-speech-settings';
import { SpeechService } from '@services/speech.service';
import { ScriptService } from '@services/script.service';
import { NoteAudioType } from '@models/note-audio-type.enum';

@Component({
  selector: 'app-speech-recorder',
  templateUrl: './speech-recorder.component.html',
  styleUrls: ['./speech-recorder.component.less'],
})
export class SpeechRecorderComponent implements OnInit {
  settings: AzureSpeechSettings;
  SpeechSDK: any;
  speechConfig: any;
  audioConfig: any;
  consultRecognizer: any;
  consultRecorder: any;
  transcriber: any;
  recordedChunks: BlobPart[] = [];
  transcription = '';

  constructor(private scriptService: ScriptService, private speechService: SpeechService) {}

  async ngOnInit() {
    try {
      await this.scriptService.loadScript('speechSdk');
    } catch (error) {
      console.error(error);
    }

    this.SpeechSDK = (window as any).SpeechSDK;
    if (!this.SpeechSDK) throw new Error('SpeechSDK failed to load properly');

    this.speechService.getAzureSpeechSettings().subscribe((settings) => {
      this.settings = settings;
      this.speechConfig = this.SpeechSDK.SpeechConfig.fromSubscription(this.settings.key, this.settings.region);
      this.speechConfig.speechRecognitionLanguage = 'en-US';
      this.speechConfig.setProfanity(this.SpeechSDK.ProfanityOption.Raw);
      //this.speechConfig.setProperty('DifferentiateGuestSpeakers', 'true');

      this.audioConfig = this.SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
    });
  }

  // record consultation audio
  async startRecordingConsult(consultationAudioType: NoteAudioType) {
    this.transcription = '';
    this.recordedChunks = [];

    try {
      if (this.transcriber) {
        this.transcriber.stopTranscribingAsync(() => this.transcriber.close());
      }
      if (this.consultRecognizer) {
        this.consultRecognizer.stopContinuousRecognitionAsync(() => this.consultRecognizer.close());
      }
      if (consultationAudioType === NoteAudioType.Consultation) {
        this.transcriber = new this.SpeechSDK.ConversationTranscriber(this.speechConfig, this.audioConfig);
        this.transcriber.sessionStarted = (s, e) => {
          //console.log(e);
        };
        this.transcriber.sessionStopped = (s, e) => {
          //console.log(e);
        };
        this.transcriber.canceled = (s, e) => {
          //console.log(e);
        };
        this.transcriber.transcribing = (s, e) => {
          // Uncomment line below to see intermediate results in console
          //console.log(e);
        };
        // For transcription, you will get one recognized event for each phrase recognized.
        this.transcriber.transcribed = (s, e) => {
          // Indicates that recognizable speech was not detected, and that recognition is done.
          if (e.result.reason === this.SpeechSDK.ResultReason.NoMatch) {
            var noMatchDetail = this.SpeechSDK.NoMatchDetails.fromResult(e.result);
            var message =
              'Reason: ' +
              this.SpeechSDK.ResultReason[e.result.reason] +
              ' NoMatchReason: ' +
              this.SpeechSDK.NoMatchReason[noMatchDetail.reason] +
              '\r\n';
            console.log(message);
          } else {
            var message = e.result.speakerId + ': ' + e.result.text + '\r\n';
            this.transcription += message;
            console.log(`TRANSCRIBED: ${message}`);
          }
        };

        this.transcriber.startTranscribingAsync(() => {
          console.log('Starting Transcribing');
        });
      } else if (consultationAudioType === NoteAudioType.Dictation) {
        this.consultRecognizer = new this.SpeechSDK.SpeechRecognizer(this.speechConfig);
        this.consultRecognizer.recognized = (s, e) => {
          if (e.result.text != null) {
            this.transcription += e.result.text;
            console.log(`RECOGNIZED: ${e.result.text}`);
          }
        };
        this.consultRecognizer.recognizing = (s, e) => {
          //console.log(`RECOGNIZING: ${e.result.text}`);
        };
        this.consultRecognizer.startContinuousRecognitionAsync(() => {
          console.log('Starting Recognizing');
        });
      }

      // Start capturing audio from the microphone
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.consultRecorder = new MediaRecorder(stream);
      this.consultRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };
      this.consultRecorder.start();
      console.log('Starting Recording');
    } catch (error) {
      console.error('Error starting recording:', error);
      throw error;
    }
  }

  // stop recording consultation, send audio to Azure and send audio and transcript to the API
  stopRecordingConsult(consultationAudioType: NoteAudioType): Promise<{ transcription: string; base64Audio: string }> {
    return new Promise(async (resolve, reject) => {
      try {
        let base64Audio = '';
        let transcription = '';

        // Stop capturing audio and process it
        if (this.consultRecorder && this.consultRecorder.state !== 'inactive') {
          // Wait for the onstop event to ensure all recordedChunks are collected.
          this.consultRecorder.onstop = async () => {
            // Stop the media stream tracks to release the microphone.
            this.consultRecorder.stream.getTracks().forEach((track) => track.stop());
            // Now create the blob once all chunks have been collected.
            const audioBlob = new Blob(this.recordedChunks, { type: 'audio/mpeg' });
            base64Audio = await this.getBase64(audioBlob);
            // Handle transcription based on the type.
            if (consultationAudioType === NoteAudioType.Consultation) {
              this.transcriber.stopTranscribingAsync(() => {
                this.transcriber.close();
                transcription = this.transcription.trim();
                resolve({ transcription, base64Audio });
              });
            } else if (consultationAudioType === NoteAudioType.Dictation && this.consultRecognizer) {
              this.consultRecognizer.stopContinuousRecognitionAsync(() => {
                this.consultRecognizer.close();
                transcription = this.transcription.trim();
                resolve({ transcription, base64Audio });
              });
            }
          };
          this.consultRecorder.stop();
        }
      } catch (error) {
        console.error('Error stopping recording:', error);
        throw error;
      }
    });
  }

  // Helper function to get base64 string from Blob
  private getBase64(blob: Blob): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => resolve(reader.result as string);
    });
  }
}
